import React, { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { useTranslation } from "react-i18next"
import { Form, ListGroup, Spinner, InputGroup, Button } from "react-bootstrap"
import * as Icon from "react-bootstrap-icons"
import { usePartner } from "@src/partner"
import { useLocalization } from "@src/localization"
import { SLIM_DEVICE_TYPE } from "@src/devices/types"
import { GET_DEVICE_PARAMS, getDevice } from "@src/api"
import { config } from "@src/constants"
import { PaginationItem } from "@src/components"

export type TrackerSelectorProps = {
  selectedSerials: string[]
  onChange: (serials: string[]) => void
  heightPx?: number
  onChangeDetails?: (details: { serialnumber: string; name: string }[]) => void
  allowFilter?: boolean
  onDevicesLoaded?: (devices: SLIM_DEVICE_TYPE[]) => void
}

const styles = {
  listContainer: (heightPx: number) => ({
    overflow: "auto" as const,
    border: "1px solid #dee2e6",
    borderRadius: 6,
    height: `${heightPx}px`,
  }),
}

export const TrackerSelector: React.FC<TrackerSelectorProps> = ({
  selectedSerials,
  onChange,
  heightPx = 360,
  onChangeDetails,
  allowFilter = false,
  onDevicesLoaded,
}) => {
  const { t } = useTranslation()
  const { partner } = usePartner()

  const [searchText, setSearchText] = useState<string>("")
  const [loading, setLoading] = useState<boolean>(false)
  const [devices, setDevices] = useState<SLIM_DEVICE_TYPE[]>([])
  const [selectedNameBySerial, setSelectedNameBySerial] = useState<Record<string, string>>({})
  const [activePage, setActivePage] = useState<number>(0)
  const [pagesCount, setPagesCount] = useState<number>(1)
  const { formatDate } = useLocalization()

  type ServiceFilterMode = "all" | "no_service" | "expires_90d"
  const [serviceFilter, setServiceFilter] = useState<ServiceFilterMode>("all")

  // Stabilize external callback to avoid recreating fetch deps and loops
  const onDevicesLoadedRef = useRef<TrackerSelectorProps["onDevicesLoaded"]>(onDevicesLoaded)
  useEffect(() => {
    onDevicesLoadedRef.current = onDevicesLoaded
  }, [onDevicesLoaded])

  // Internal selected set to avoid full re-render on every click; only parent state changes when needed
  const [internalSelected, setInternalSelected] = useState<Set<string>>(
    () => new Set(selectedSerials.map((s) => s.trim()).filter(Boolean)),
  )
  useEffect(() => {
    // keep internal state in sync when parent changes via external means
    setInternalSelected(new Set(selectedSerials.map((s) => s.trim()).filter(Boolean)))
  }, [selectedSerials])

  const isUnderOneMonth = (endDate: Date): boolean => {
    const diffMs = endDate.getTime() - Date.now()
    const diffDays = Math.ceil(diffMs / (1000 * 60 * 60 * 24))
    return diffDays > 0 && diffDays < 30
  }

  const exp90Date = useMemo(() => {
    const d = new Date()
    d.setDate(d.getDate() + 90)
    const yyyy = d.getFullYear()
    const mm = String(d.getMonth() + 1).padStart(2, "0")
    const dd = String(d.getDate()).padStart(2, "0")
    return `${yyyy}-${mm}-${dd}`
  }, [])

  const fetchDevices = useCallback(() => {
    if (!partner?.uuid) return
    setLoading(true)
    const params: GET_DEVICE_PARAMS = {
      page: activePage,
      per_page: config.itemsPerPage,
      search_string: searchText,
    }
    if (serviceFilter === "no_service") {
      params.servicebooking_status = "none"
    } else if (serviceFilter === "expires_90d") {
      params.servicebooking_status = "active"
      params.servicebooking_end_before = exp90Date
    }
    getDevice(partner.uuid, null, params).then(({ response, data }) => {
      if (response.ok) {
        setDevices(data.devices)
        if (data.meta?.per_page && data.meta?.total != null) {
          setPagesCount(Math.max(1, Math.ceil(data.meta.total / data.meta.per_page)))
        } else {
          setPagesCount(1)
        }
        if (onDevicesLoadedRef.current) onDevicesLoadedRef.current(data.devices)
      }
      setLoading(false)
    })
  }, [partner?.uuid, activePage, searchText, serviceFilter, exp90Date])

  useEffect(() => {
    fetchDevices()
  }, [fetchDevices])

  // Enrich selected name mapping when devices or selection change, without refetch
  useEffect(() => {
    if (!devices?.length || !selectedSerials?.length) return
    setSelectedNameBySerial((prev) => {
      const next = { ...prev }
      for (const d of devices) {
        if (selectedSerials.includes(d.serialnumber)) next[d.serialnumber] = d.name
      }
      return next
    })
  }, [devices, selectedSerials])

  const toggleSerial = (serial: string) => {
    const next = new Set(internalSelected)
    if (next.has(serial)) {
      next.delete(serial)
    } else {
      next.add(serial)
      const device = devices.find((d) => d.serialnumber === serial)
      if (device) setSelectedNameBySerial((prev) => ({ ...prev, [serial]: device.name }))
    }
    const arrayNext = Array.from(next)
    setInternalSelected(next)
    onChange(arrayNext)
    if (onChangeDetails) {
      const details = arrayNext.map((sn) => ({
        serialnumber: sn,
        name: selectedNameBySerial[sn] || "",
      }))
      onChangeDetails(details)
    }
  }

  const onChangePage = (page: number) => {
    if (page < 0 || page >= pagesCount) return
    setActivePage(page)
  }

  useEffect(() => {
    // prune removed and notify details consumer
    setSelectedNameBySerial((prev) => {
      const next: Record<string, string> = {}
      for (const sn of selectedSerials) if (prev[sn]) next[sn] = prev[sn]
      return next
    })
    if (onChangeDetails) {
      const details = selectedSerials.map((sn) => ({
        serialnumber: sn,
        name: selectedNameBySerial[sn] || "",
      }))
      onChangeDetails(details)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedSerials])

  return (
    <div>
      <div className="d-flex flex-wrap gap-2 mb-2 align-items-center">
        {allowFilter && (
          <>
            <div className="me-2">{t("settings.partner.filter")}:</div>
            <div
              className="btn-group btn-group-sm"
              role="group"
              aria-label={t("devices_screen.filter.service_group") || "Service filter"}
            >
              <button
                type="button"
                className={`btn ${serviceFilter === "no_service" ? "btn-primary" : "btn-outline-secondary"}`}
                onClick={() => {
                  setActivePage(0)
                  setServiceFilter(serviceFilter === "no_service" ? "all" : "no_service")
                }}
              >
                {t("devices_screen.filter.service_none")}
              </button>

              <button
                type="button"
                className={`btn ${serviceFilter === "expires_90d" ? "btn-primary" : "btn-outline-secondary"}`}
                onClick={() => {
                  setActivePage(0)
                  setServiceFilter(serviceFilter === "expires_90d" ? "all" : "expires_90d")
                }}
              >
                {t("devices_screen.filter.service_exp_90d")}
              </button>
            </div>
          </>
        )}
        <InputGroup>
          <Form.Control
            type="search"
            id="tracker-search"
            autoComplete="off"
            placeholder={t("sidebar.search_placeholder")}
            value={searchText}
            onChange={(e) => {
              setActivePage(0)
              setSearchText(e.target.value)
            }}
          />
          {searchText && (
            <Button
              type="button"
              aria-label="Clear search"
              variant="outline-secondary"
              className="d-flex align-items-center"
              onClick={() => {
                setActivePage(0)
                setSearchText("")
              }}
            >
              <Icon.X size={16} />
            </Button>
          )}
        </InputGroup>
      </div>
      <div className="mt-2" style={styles.listContainer(heightPx)}>
        {loading ? (
          <div className="d-flex justify-content-center mt-4">
            <Spinner size="sm" animation="border" />
          </div>
        ) : (
          <ListGroup variant="flush">
            {devices.map((device) => {
              const isChecked = internalSelected.has(device.serialnumber)
              const endIso = device?.servicebooking?.service_end_date
              let serviceBadge: React.ReactNode
              if (!endIso) {
                serviceBadge = (
                  <span className="badge bg-danger ms-2">
                    {t("device_screen.details_tab.no_service")}
                  </span>
                )
              } else {
                const endDate = new Date(endIso)
                if (Number.isNaN(endDate.getTime()) || endDate.getTime() <= Date.now()) {
                  serviceBadge = (
                    <span className="badge bg-danger ms-2">
                      {t("device_screen.details_tab.no_service")}
                    </span>
                  )
                } else {
                  const badgeClass = isUnderOneMonth(endDate)
                    ? "badge bg-warning text-dark"
                    : "badge bg-secondary"
                  serviceBadge = (
                    <span className={`${badgeClass} ms-2`}>
                      {t("devices_screen.active_service")}: {formatDate(endDate)}
                    </span>
                  )
                }
              }
              return (
                <ListGroup.Item
                  key={device.serialnumber}
                  action
                  onClick={() => toggleSerial(device.serialnumber)}
                >
                  <div className="d-flex align-items-center">
                    <input
                      type="checkbox"
                      className="form-check-input me-2"
                      checked={isChecked}
                      onChange={() => toggleSerial(device.serialnumber)}
                      onClick={(e) => e.stopPropagation()}
                    />
                    <span>
                      {device.serialnumber} - {device.name}
                    </span>
                    {serviceBadge}
                  </div>
                </ListGroup.Item>
              )
            })}
            {devices.length === 0 && (
              <ListGroup.Item>{t("sidebar.no_device_found")}</ListGroup.Item>
            )}
          </ListGroup>
        )}
      </div>
      <div className="d-flex justify-content-end align-items-center mt-2 mb-0">
        <PaginationItem
          activePage={activePage}
          pagesCount={pagesCount}
          onChangePage={onChangePage}
        />
      </div>
    </div>
  )
}
